import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Search, MessageCircle, Bot, Compass, ChevronDown, ChevronUp,
  Trophy, Eye, TrendingUp, Users, Sparkles, Cpu, Zap,
  CheckCircle2, XCircle, Terminal, Lock, Columns, ArrowRight,
  Globe, MapPin, Star, Facebook, Instagram, Linkedin, ListChecks,
  AlertTriangle, ChevronRight
} from 'lucide-react';
import axios from 'axios';
import AppShell from '@/components/AppShell';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ENGINE_ICONS = {
  chatgpt: MessageCircle,
  claude: Bot,
  gemini: Sparkles,
  perplexity: Compass,
  google_ai: Search,
  copilot: Cpu,
  grok: Zap,
};

const ENGINE_COLORS = {
  chatgpt: '#10A37F',
  claude: '#D97706',
  gemini: '#4285F4',
  perplexity: '#1B9AAA',
  google_ai: '#EA4335',
  copilot: '#0078D4',
  grok: '#3C3226',
};

export default function Visibility() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [scoreHistory, setScoreHistory] = useState([]);
  const [competitors, setCompetitors] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeEngine, setActiveEngine] = useState('chatgpt');
  const [expandedQuery, setExpandedQuery] = useState(null);
  const [showComparison, setShowComparison] = useState(false);
  const [signalHealth, setSignalHealth] = useState(null);
  const [competitiveGap, setCompetitiveGap] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const [visRes, histRes, compRes, sigRes, gapRes] = await Promise.all([
        axios.get(`${API}/visibility`, { withCredentials: true }),
        axios.get(`${API}/score/history`, { withCredentials: true }).catch(() => ({ data: { history: [] } })),
        axios.get(`${API}/competitors`, { withCredentials: true }).catch(() => ({ data: { competitors: [], can_track: false } })),
        axios.get(`${API}/signal-health`, { withCredentials: true }).catch(() => ({ data: {} })),
        axios.get(`${API}/competitive-gap`, { withCredentials: true }).catch(() => ({ data: {} })),
      ]);
      setData(visRes.data);
      setScoreHistory(histRes.data.history || []);
      setCompetitors(compRes.data);
      setSignalHealth(sigRes.data?.health || null);
      setCompetitiveGap(gapRes.data);
    } catch (e) {
      if (e.response?.status === 401) navigate('/');
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F7F4EF] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#D4C9B8] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!data?.has_scan) {
    return (
      <div className="min-h-screen bg-[#F7F4EF] flex flex-col items-center justify-center px-6">
        <Eye size={48} strokeWidth={1} className="text-[#D4C9B8] mb-4" />
        <h2 className="font-display text-2xl text-[#3C3226] mb-2">No scan data yet</h2>
        <p className="text-[#5C5347] text-center mb-6">Start monitoring to see your AI recommendations across 9 engines.</p>
        <button onClick={() => navigate('/onboarding')} className="rounded-full bg-[#C67A5C] text-[#F0EBE3] px-8 py-3 font-medium" data-testid="run-first-scan">
          Begin monitoring
        </button>
        <AppShell active="visibility" />
      </div>
    );
  }

  const { ai_platforms, leaderboard, channels, score, engine_queries, engine_scores } = data;
  const activeEngines = (ai_platforms || []).filter(p => p.active);
  const inactiveEngines = (ai_platforms || []).filter(p => !p.active);

  return (
    <AppShell active="visibility">
    <div className="min-h-screen bg-[#F7F4EF] pb-24 desktop:pb-8" data-testid="visibility-page">
      <header className="px-6 pt-6 pb-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-display text-2xl font-medium text-[#3C3226]">Your AI Recommendations</h1>
            <p className="text-sm text-[#5C5347]">7-engine AI presence scan</p>
          </div>
          <button
            onClick={() => setShowComparison(!showComparison)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
              showComparison ? 'bg-[#3C3226] text-[#F0EBE3]' : 'bg-[#F7F4EF] text-[#5C5347] hover:bg-[#D4C9B8]'
            }`}
            data-testid="compare-toggle"
          >
            <Columns size={13} />
            Compare
          </button>
        </div>
      </header>

      <main className="px-6 max-w-lg mx-auto tablet:max-w-2xl desktop:max-w-5xl space-y-5">
        {/* AI Presence Map — All 7 engines */}
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="p-5 rounded-sm bg-[#F7F4EF] border border-[#D4C9B8]" data-testid="ai-presence-map">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-medium text-[#3C3226]">Who AI Mentions</h3>
            <span className="text-[10px] text-[#5C5347]">{activeEngines.length} active / {ai_platforms?.length || 0} total</span>
          </div>

          {/* Active engines — top row */}
          <div className="grid grid-cols-4 gap-2 mb-2.5">
            {(ai_platforms || []).slice(0, 4).map((platform) => (
              <EngineCard
                key={platform.engine}
                platform={platform}
                isActive={activeEngine === platform.engine}
                onClick={() => { if (platform.active) setActiveEngine(platform.engine); }}
              />
            ))}
          </div>
          {/* Bottom row — remaining engines */}
          <div className="grid grid-cols-3 gap-2">
            {(ai_platforms || []).slice(4).map((platform) => (
              <EngineCard
                key={platform.engine}
                platform={platform}
                isActive={activeEngine === platform.engine}
                onClick={() => { if (platform.active) setActiveEngine(platform.engine); }}
              />
            ))}
          </div>
          <p className="mt-2 text-center" style={{ fontSize: '9px', color: '#8C7E6E', fontStyle: 'italic' }}>Siri and Alexa results are estimated based on signals from other AI platforms, as Apple and Amazon do not offer public query APIs.</p>
        </motion.div>

        {/* Side-by-side Comparison View */}
        <AnimatePresence>
          {showComparison && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="overflow-hidden"
            >
              <EngineComparison platforms={ai_platforms} engineQueries={engine_queries} engineScores={engine_scores} />
            </motion.div>
          )}
        </AnimatePresence>

        {/* Engine Detail — Terminal-style AI Response Viewer */}
        {!showComparison && (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}>
            <EngineTerminal
              engineKey={activeEngine}
              queries={engine_queries?.[activeEngine] || []}
              engineScore={engine_scores?.[activeEngine]}
              platform={(ai_platforms || []).find(p => p.engine === activeEngine)}
              expandedQuery={expandedQuery}
              setExpandedQuery={setExpandedQuery}
            />
          </motion.div>
        )}

        {/* How AI Decides (Change 4) */}
        {signalHealth && (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.08 }}>
            <SignalStrength health={signalHealth} />
          </motion.div>
        )}

        {/* What's Holding You Back (Change 5) */}
        {competitiveGap?.comparisons?.length > 0 && (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.09 }}>
            <CompetitiveGap data={competitiveGap} />
          </motion.div>
        )}

        {/* Score Trend */}
        {scoreHistory.length > 0 && (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="p-5 rounded-sm bg-[#F7F4EF] border border-[#D4C9B8]">
            <div className="flex items-center gap-2 mb-4">
              <TrendingUp size={16} strokeWidth={1.5} className="text-[#1D6B4F]" />
              <h3 className="text-sm font-medium text-[#3C3226]">Score Trend</h3>
            </div>
            <ScoreTrendChart history={scoreHistory} />
          </motion.div>
        )}

        {/* Competitor Leaderboard */}
        {leaderboard?.length > 0 && (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }} className="p-5 rounded-sm bg-[#F7F4EF] border border-[#D4C9B8]">
            <h3 className="text-sm font-medium text-[#3C3226] mb-1">Who AI mentions most</h3>
            <p className="text-xs text-[#5C5347] mb-4">Based on {data.total_queries} queries across {activeEngines.length} engines</p>
            <div className="space-y-2">
              {leaderboard.map((entry, i) => (
                <div
                  key={entry.name || `lb-${i}`}
                  className={`flex items-center gap-3 p-3 rounded-sm transition-colors ${entry.is_user ? 'bg-[#1D6B4F]/5 border border-[#1D6B4F]/20' : ''}`}
                  data-testid={`leaderboard-entry-${i}`}
                >
                  <span className={`text-sm font-semibold w-6 text-center ${entry.rank === 1 ? 'text-[#EF9F27]' : 'text-[#5C5347]'}`}>
                    {entry.rank === 1 ? <Trophy size={16} className="text-[#EF9F27] inline" strokeWidth={1.5} /> : `#${entry.rank}`}
                  </span>
                  <span className={`flex-1 text-sm ${entry.is_user ? 'font-semibold text-[#1D6B4F]' : 'text-[#3C3226]'}`}>
                    {entry.is_user ? `${entry.name} (You)` : entry.name}
                  </span>
                  <span className="text-xs text-[#5C5347]">{entry.mentions} mentions</span>
                </div>
              ))}
            </div>
          </motion.div>
        )}

        {/* Tracked Competitors */}
        {competitors?.competitors?.length > 0 && (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
            <div className="p-5 rounded-sm bg-[#F7F4EF] border border-[#D4C9B8]">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-sm font-medium text-[#3C3226]">Competitor Tracker</h3>
                  <p className="text-xs text-[#5C5347]">{competitors.tracked_count}/{competitors.max_tracked || 5} tracked</p>
                </div>
                <Users size={16} strokeWidth={1.5} className="text-[#C67A5C]" />
              </div>
              <div className="space-y-2">
                {competitors.competitors.map((comp, i) => (
                  <div key={comp.name || `comp-${i}`} className="flex items-center gap-3 py-2" data-testid={`competitor-${i}`}>
                    <div className={`w-8 h-8 rounded-none flex items-center justify-center text-xs font-bold ${
                      (comp.estimated_score || 0) > (competitors.user_score || 0) ? 'bg-[#E24B4A]/10 text-[#E24B4A]' : 'bg-[#1D6B4F]/10 text-[#1D6B4F]'
                    }`}>
                      {comp.estimated_score || '\u2014'}
                    </div>
                    <div className="flex-1 min-w-0">
                      <span className="text-sm font-medium text-[#3C3226] block truncate">{comp.name}</span>
                      <span className="text-xs text-[#5C5347]">{comp.mentions || 0} AI mentions</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        )}

        {/* Channel Breakdown */}
        {channels?.length > 0 && (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25 }} className="p-5 rounded-sm bg-[#F7F4EF] border border-[#D4C9B8]">
            <h3 className="text-sm font-medium text-[#3C3226] mb-4">Score Breakdown</h3>
            <div className="space-y-3">
              {channels.map((ch, i) => (
                <div key={ch.label || `ch-${i}`}>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-[#5C5347]">{ch.label}</span>
                    <span className="font-medium text-[#3C3226]">{ch.score}/100</span>
                  </div>
                  <div className="h-1.5 bg-[#F7F4EF] rounded-full overflow-hidden">
                    <div
                      className="h-full rounded-none transition-all duration-700"
                      style={{ width: `${ch.score}%`, backgroundColor: ch.score >= 60 ? '#1D6B4F' : ch.score >= 35 ? '#EF9F27' : '#E24B4A' }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        )}
      </main>
    </div>
    </AppShell>
  );
}


/* ── Engine Card for Presence Map ── */
function EngineCard({ platform, isActive, onClick }) {
  const Icon = ENGINE_ICONS[platform.engine] || Cpu;
  const color = platform.color || ENGINE_COLORS[platform.engine] || '#888';
  const isActivatable = platform.status === 'activate' || platform.status === 'skipped';
  const isLive = platform.active;

  const statusIndicator = () => {
    switch (platform.status) {
      case 'mentioned':
        return <CheckCircle2 size={11} className="text-[#1D6B4F]" />;
      case 'not_mentioned':
        return <XCircle size={11} className="text-[#E24B4A]" />;
      case 'activate':
        return <Lock size={11} className="text-[#C67A5C]" />;
      case 'skipped':
        return <span className="text-[9px] text-[#5C5347]/60 font-semibold">TEST</span>;
      default:
        return <span className="w-2 h-2 rounded-full bg-[#D4C9B8] inline-block" />;
    }
  };

  return (
    <button
      onClick={onClick}
      disabled={!isLive}
      className={`relative p-2.5 rounded-sm border transition-all text-left ${
        isActive && isLive
          ? 'border-[#3C3226] bg-[#F7F4EF] shadow-sm'
          : isActivatable
            ? 'border-dashed border-[#D4C9B8] bg-[#F7F4EF] opacity-70'
            : 'border-transparent bg-[#F8F5EF] hover:bg-[#EDE8DF]'
      } ${!isLive ? 'cursor-default' : 'cursor-pointer'}`}
      data-testid={`engine-tab-${platform.engine}`}
    >
      <div className="flex items-center gap-1 mb-1.5">
        <Icon size={12} strokeWidth={1.5} style={{ color: isActivatable ? '#888' : color }} />
        <span className={`text-[10px] font-semibold truncate ${isActivatable ? 'text-[#5C5347]/60' : 'text-[#3C3226]'}`}>
          {platform.name}
        </span>
      </div>
      <div className="flex items-center gap-1">
        {statusIndicator()}
        {isLive && platform.mentions != null && (
          <span className={`text-[10px] font-medium ${
            platform.status === 'mentioned' ? 'text-[#1D6B4F]' : 'text-[#E24B4A]'
          }`}>
            {platform.mentions}/{platform.total_queries}
          </span>
        )}
        {platform.status === 'activate' && (
          <span className="text-[9px] text-[#C67A5C] font-semibold">Activate</span>
        )}
      </div>
    </button>
  );
}


/* ── Engine Terminal — dark-themed AI response viewer ── */
function EngineTerminal({ engineKey, queries, engineScore, platform, expandedQuery, setExpandedQuery }) {
  const Icon = ENGINE_ICONS[engineKey] || Cpu;
  const color = ENGINE_COLORS[engineKey] || '#888';
  const label = platform?.name || engineKey;
  const mentionPct = engineScore?.mention_pct || 0;
  const mentions = engineScore?.mentions || 0;
  const totalQ = engineScore?.total_queries || 0;

  // If engine requires activation
  if (platform?.status === 'activate') {
    return (
      <div className="rounded-sm overflow-hidden border border-dashed border-[#D4C9B8]" data-testid={`engine-terminal-${engineKey}`}>
        <div className="bg-[#F7F4EF] px-5 py-8 text-center">
          <Lock size={24} className="text-[#C67A5C] mx-auto mb-3" />
          <h4 className="text-sm font-medium text-[#3C3226] mb-1">{label} scanning ready</h4>
          <p className="text-xs text-[#5C5347] mb-3">Add your API key to see how {label} sees your business.</p>
          <div className="inline-flex items-center gap-1.5 text-xs text-[#C67A5C] font-medium">
            <ArrowRight size={12} /> Settings &rarr; API Keys
          </div>
        </div>
      </div>
    );
  }

  // If skipped in test mode
  if (platform?.status === 'skipped') {
    return (
      <div className="rounded-sm overflow-hidden border border-[#D4C9B8]" data-testid={`engine-terminal-${engineKey}`}>
        <div className="bg-[#F8F5EF] px-5 py-6 text-center">
          <Terminal size={20} className="text-[#5C5347]/40 mx-auto mb-2" />
          <p className="text-xs text-[#5C5347]">{label} skipped in test mode.</p>
          <p className="text-[10px] text-[#5C5347]/60 mt-1">Switch to production to scan all 7 engines.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-sm overflow-hidden border border-[#2A2A2A]" data-testid={`engine-terminal-${engineKey}`}>
      {/* Terminal header */}
      <div className="bg-[#3C3226] px-5 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="flex gap-1.5">
            <div className="w-2.5 h-2.5 rounded-full bg-[#E24B4A]" />
            <div className="w-2.5 h-2.5 rounded-full bg-[#EF9F27]" />
            <div className="w-2.5 h-2.5 rounded-full bg-[#1D6B4F]" />
          </div>
          <div className="flex items-center gap-1.5">
            <Icon size={14} style={{ color }} />
            <span className="text-sm font-mono font-medium text-[#F0EBE3]/90">{label}</span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {mentions > 0 ? (
            <span className="text-xs font-mono px-2 py-0.5 rounded-none bg-[#1D6B4F]/20 text-[#1D6B4F]">
              {mentions}/{totalQ} ({mentionPct}%)
            </span>
          ) : totalQ > 0 ? (
            <span className="text-xs font-mono px-2 py-0.5 rounded-none bg-[#E24B4A]/20 text-[#E24B4A]">
              0 mentions
            </span>
          ) : (
            <span className="text-xs font-mono px-2 py-0.5 rounded-none bg-[#F7F4EF]/10 text-[#F0EBE3]/40">
              awaiting scan
            </span>
          )}
        </div>
      </div>

      {/* Terminal body */}
      <div className="bg-[#111111] px-5 py-4 max-h-[360px] overflow-y-auto">
        {queries.length === 0 ? (
          <div className="text-center py-6">
            <Terminal size={24} className="text-[#F0EBE3]/20 mx-auto mb-2" />
            <p className="text-xs font-mono text-[#F0EBE3]/30">No queries recorded for {label} yet.</p>
            <p className="text-xs font-mono text-[#F0EBE3]/20 mt-1">Start monitoring to see results.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {queries.map((q, i) => (
              <div key={`${q.prompt?.slice(0, 20)}-${i}`} className="group">
                <button
                  onClick={() => setExpandedQuery(expandedQuery === `${engineKey}-${i}` ? null : `${engineKey}-${i}`)}
                  className="w-full text-left flex items-start gap-2"
                  data-testid={`terminal-query-${engineKey}-${i}`}
                >
                  <span className="text-[#1D6B4F] font-mono text-xs mt-0.5 flex-shrink-0">$</span>
                  <span className="text-xs font-mono text-[#F0EBE3]/80 flex-1 leading-relaxed">{q.prompt}</span>
                  <div className="flex items-center gap-1.5 flex-shrink-0 mt-0.5">
                    {q.business_mentioned ? (
                      <CheckCircle2 size={12} className="text-[#1D6B4F]" />
                    ) : (
                      <XCircle size={12} className="text-[#E24B4A]/60" />
                    )}
                    {expandedQuery === `${engineKey}-${i}` ? (
                      <ChevronUp size={12} className="text-[#F0EBE3]/30" />
                    ) : (
                      <ChevronDown size={12} className="text-[#F0EBE3]/30" />
                    )}
                  </div>
                </button>
                <AnimatePresence>
                  {expandedQuery === `${engineKey}-${i}` && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.15 }}
                      className="overflow-hidden"
                    >
                      <div className="ml-4 mt-2 pl-3 border-l border-white/10">
                        <p className="text-xs font-mono text-[#F0EBE3]/50 leading-relaxed whitespace-pre-wrap">{q.response_snippet}</p>
                        <div className="mt-2 flex items-center gap-2">
                          <span className={`text-[10px] font-mono px-1.5 py-0.5 rounded ${
                            q.business_mentioned
                              ? 'bg-[#1D6B4F]/15 text-[#1D6B4F]'
                              : 'bg-[#E24B4A]/15 text-[#E24B4A]/70'
                          }`}>
                            {q.business_mentioned ? 'MENTIONED' : 'NOT FOUND'}
                          </span>
                          <span className="text-[10px] font-mono text-[#F0EBE3]/20">{q.type}</span>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}


/* ── Side-by-side Engine Comparison ── */
function EngineComparison({ platforms, engineQueries, engineScores }) {
  const liveEngines = (platforms || []).filter(p => p.active && p.total_queries > 0);

  if (liveEngines.length < 2) {
    return (
      <div className="p-5 rounded-sm bg-[#F7F4EF] border border-[#D4C9B8] text-center" data-testid="engine-comparison">
        <Columns size={24} className="text-[#D4C9B8] mx-auto mb-2" />
        <p className="text-sm text-[#5C5347]">Need at least 2 engines with results to compare.</p>
        <p className="text-xs text-[#5C5347]/60 mt-1">Start monitoring to populate engine data.</p>
      </div>
    );
  }

  // Find first shared prompt across engines
  const firstEngineQueries = engineQueries?.[liveEngines[0].engine] || [];
  const sharedPrompt = firstEngineQueries[0]?.prompt || '';

  return (
    <div className="p-5 rounded-sm bg-[#F7F4EF] border border-[#D4C9B8]" data-testid="engine-comparison">
      <h3 className="text-sm font-medium text-[#3C3226] mb-1">Engine Comparison</h3>
      <p className="text-xs text-[#5C5347] mb-4">How each AI engine sees your business differently</p>

      {/* Mention rate comparison bars */}
      <div className="space-y-2.5 mb-5">
        {(platforms || []).map((p) => {
          const Icon = ENGINE_ICONS[p.engine] || Cpu;
          const color = p.color || ENGINE_COLORS[p.engine] || '#888';
          const isActivate = p.status === 'activate' || p.status === 'skipped';
          const pct = p.mention_pct || 0;
          const weight = p.weight || 0;

          return (
            <div key={p.engine} className="flex items-center gap-3" data-testid={`compare-bar-${p.engine}`}>
              <div className="w-20 flex items-center gap-1.5 flex-shrink-0">
                <Icon size={12} style={{ color: isActivate ? '#888' : color }} />
                <span className={`text-[10px] font-semibold ${isActivate ? 'text-[#5C5347]/50' : 'text-[#3C3226]'}`}>{p.name}</span>
              </div>
              <div className="flex-1 h-3 bg-[#F7F4EF] rounded-full overflow-hidden">
                {isActivate ? (
                  <div className="h-full rounded-full bg-[#D4C9B8] w-full" style={{ backgroundImage: 'repeating-linear-gradient(90deg, transparent, transparent 4px, #FFFFFF 4px, #FFFFFF 8px)' }} />
                ) : (
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: `${pct}%` }}
                    transition={{ duration: 0.8, ease: 'easeOut' }}
                    className="h-full rounded-full"
                    style={{ backgroundColor: color }}
                  />
                )}
              </div>
              <div className="w-12 text-right flex-shrink-0">
                {isActivate ? (
                  <Lock size={10} className="text-[#C67A5C] inline" />
                ) : (
                  <span className="text-[10px] font-semibold" style={{ color }}>{pct}%</span>
                )}
              </div>
              <span className="text-[9px] text-[#5C5347]/40 w-8 text-right flex-shrink-0">{Math.round(weight * 100)}%w</span>
            </div>
          );
        })}
      </div>

      {/* Sample response comparison (first shared prompt) */}
      {sharedPrompt && (
        <div className="border-t border-[#D4C9B8] pt-4">
          <p className="text-[10px] font-mono text-[#5C5347] mb-3 leading-relaxed">$ {sharedPrompt}</p>
          <div className="grid grid-cols-1 gap-2">
            {liveEngines.slice(0, 3).map((eng) => {
              const Icon = ENGINE_ICONS[eng.engine] || Cpu;
              const color = eng.color || ENGINE_COLORS[eng.engine] || '#888';
              const queries = engineQueries?.[eng.engine] || [];
              const matchQ = queries.find(q => q.prompt === sharedPrompt) || queries[0];
              const snippet = matchQ?.response_snippet || 'No response';
              const mentioned = matchQ?.business_mentioned;

              return (
                <div key={eng.engine} className="p-3 rounded-sm bg-[#F8F5EF]" data-testid={`compare-response-${eng.engine}`}>
                  <div className="flex items-center gap-1.5 mb-1.5">
                    <Icon size={11} style={{ color }} />
                    <span className="text-[10px] font-semibold text-[#3C3226]">{eng.name}</span>
                    {mentioned != null && (
                      mentioned ? <CheckCircle2 size={10} className="text-[#1D6B4F] ml-auto" /> : <XCircle size={10} className="text-[#E24B4A]/60 ml-auto" />
                    )}
                  </div>
                  <p className="text-[10px] text-[#5C5347] leading-relaxed line-clamp-3">{snippet}</p>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}


/* ── Mini Score Trend Chart (SVG) ── */
function ScoreTrendChart({ history }) {
  if (!history || history.length === 0) return null;

  const width = 300;
  const height = 100;
  const padding = { top: 10, right: 10, bottom: 20, left: 30 };
  const chartW = width - padding.left - padding.right;
  const chartH = height - padding.top - padding.bottom;

  const scores = history.map(h => h.score);
  const minScore = Math.max(0, Math.min(...scores) - 10);
  const maxScore = Math.min(100, Math.max(...scores) + 10);
  const range = maxScore - minScore || 1;

  const points = history.map((h, i) => ({
    x: padding.left + (history.length > 1 ? (i / (history.length - 1)) * chartW : chartW / 2),
    y: padding.top + chartH - ((h.score - minScore) / range) * chartH,
    score: h.score,
    date: h.date,
    color: h.color,
  }));

  const pathD = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ');
  const areaD = pathD + ` L ${points[points.length - 1].x} ${padding.top + chartH} L ${points[0].x} ${padding.top + chartH} Z`;

  const latest = points[points.length - 1];
  const first = points[0];
  const change = latest.score - first.score;

  return (
    <div data-testid="score-trend-chart">
      <svg viewBox={`0 0 ${width} ${height}`} className="w-full" style={{ maxHeight: 120 }}>
        <defs>
          <linearGradient id="areaGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#1D6B4F" stopOpacity="0.15" />
            <stop offset="100%" stopColor="#1D6B4F" stopOpacity="0" />
          </linearGradient>
        </defs>
        <path d={areaD} fill="url(#areaGrad)" />
        <path d={pathD} fill="none" stroke="#1D6B4F" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        {points.map((p) => (
          <circle key={`pt-${p.x}-${p.y}`} cx={p.x} cy={p.y} r="3" fill="#FFFFFF" stroke={p.color || '#1D6B4F'} strokeWidth="2" />
        ))}
        <text x={padding.left} y={padding.top + chartH + 14} fontSize="9" fill="#888" textAnchor="start">
          {history.length > 1 ? new Date(first.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : ''}
        </text>
        <text x={padding.left + chartW} y={padding.top + chartH + 14} fontSize="9" fill="#888" textAnchor="end">
          {new Date(latest.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
        </text>
      </svg>
      <div className="flex items-center justify-between mt-1">
        <span className="text-xs text-[#5C5347]">{history.length} scan{history.length > 1 ? 's' : ''}</span>
        {history.length > 1 && (
          <span className={`text-xs font-medium ${change >= 0 ? 'text-[#1D6B4F]' : 'text-[#E24B4A]'}`}>
            {change >= 0 ? '+' : ''}{change} pts
          </span>
        )}
      </div>
    </div>
  );
}


/* ── How AI Decides (Change 4) ── */
const SIGNAL_ICONS = {
  'map-pin': MapPin,
  'globe': Globe,
  'star': Star,
  'facebook': Facebook,
  'instagram': Instagram,
  'linkedin': Linkedin,
  'list-checks': ListChecks,
};

function SignalStrength({ health }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="p-5 rounded-sm bg-[#F7F4EF] border border-[#D4C9B8]" data-testid="signal-strength">
      <div className="flex items-center justify-between mb-1">
        <h3 className="text-sm font-medium text-[#3C3226]">How AI Decides</h3>
        <span className="text-xs font-semibold text-[#3C3226]">{health.overall_score}/100</span>
      </div>
      <p className="text-[10px] text-[#5C5347] mb-3">Data sources AI uses to evaluate you</p>

      {/* Overall bar */}
      <div className="h-2 bg-[#F7F4EF] rounded-full overflow-hidden mb-4">
        <motion.div
          initial={{ width: 0 }}
          animate={{ width: `${health.overall_score}%` }}
          transition={{ duration: 0.8 }}
          className="h-full rounded-full"
          style={{ backgroundColor: health.overall_score >= 60 ? '#1D6B4F' : health.overall_score >= 35 ? '#EF9F27' : '#E24B4A' }}
        />
      </div>

      {/* Signal list */}
      <div className="space-y-2">
        {(expanded ? health.signals : health.signals.slice(0, 4)).map((sig) => {
          const Icon = SIGNAL_ICONS[sig.icon] || Globe;
          const statusColor = sig.status === 'strong' ? '#1D6B4F' : sig.status === 'needs_work' ? '#EF9F27' : '#E24B4A';
          const StatusIcon = sig.status === 'strong' ? CheckCircle2 : sig.status === 'needs_work' ? AlertTriangle : XCircle;

          return (
            <div key={sig.key} className="flex items-center gap-3 py-1.5" data-testid={`signal-${sig.key}`}>
              <StatusIcon size={14} style={{ color: statusColor }} className="flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5">
                  <Icon size={12} className="text-[#5C5347]" />
                  <span className="text-xs font-medium text-[#3C3226]">{sig.label}</span>
                  <span className="text-[10px] font-semibold ml-auto" style={{ color: statusColor }}>
                    {sig.status === 'strong' ? 'Strong' : sig.status === 'needs_work' ? 'Needs work' : 'Missing'}
                  </span>
                </div>
                <p className="text-[10px] text-[#5C5347] mt-0.5">{sig.detail}</p>
              </div>
            </div>
          );
        })}
      </div>

      {health.signals.length > 4 && (
        <button
          onClick={() => setExpanded(!expanded)}
          className="mt-3 flex items-center gap-1 text-xs text-[#1D6B4F] font-medium"
          data-testid="signal-expand-toggle"
        >
          {expanded ? 'Show less' : `Show all ${health.signals.length} signals`}
          <ChevronDown size={12} className={`transition-transform ${expanded ? 'rotate-180' : ''}`} />
        </button>
      )}

      {/* Quick stats */}
      <div className="flex gap-3 mt-3 pt-3 border-t border-[#D4C9B8]">
        <span className="text-[10px] text-[#1D6B4F] font-medium">{health.strong_count} strong</span>
        <span className="text-[10px] text-[#EF9F27] font-medium">{health.needs_work_count} needs work</span>
        <span className="text-[10px] text-[#E24B4A] font-medium">{health.missing_count} missing</span>
      </div>
    </div>
  );
}


/* ── What's Holding You Back (Change 5) ── */
function CompetitiveGap({ data }) {
  const [activeComp, setActiveComp] = useState(0);
  const comps = data.comparisons || [];
  const comp = comps[activeComp];

  if (!comp) return null;

  return (
    <div className="p-5 rounded-sm bg-[#F7F4EF] border border-[#D4C9B8]" data-testid="competitive-gap">
      <h3 className="text-sm font-medium text-[#3C3226] mb-1">What's Holding You Back</h3>
      <p className="text-[10px] text-[#5C5347] mb-3">Signal-by-signal breakdown vs. your competitors</p>

      {/* Competitor tabs */}
      {comps.length > 1 && (
        <div className="flex gap-1.5 mb-4 overflow-x-auto">
          {comps.map((c, i) => (
            <button
              key={c.competitor_name}
              onClick={() => setActiveComp(i)}
              className={`px-3 py-1.5 rounded-none text-[10px] font-medium whitespace-nowrap transition-all ${
                i === activeComp ? 'bg-[#3C3226] text-[#F0EBE3]' : 'bg-[#F7F4EF] text-[#5C5347]'
              }`}
              data-testid={`gap-tab-${i}`}
            >
              vs. {c.competitor_name.split(' ').slice(0, 2).join(' ')}
            </button>
          ))}
        </div>
      )}

      {/* Score comparison header */}
      <div className="flex items-center justify-between mb-4 p-3 rounded-sm bg-[#F8F5EF]">
        <div className="text-center">
          <p className="text-lg font-bold text-[#3C3226]">{data.user_score}</p>
          <p className="text-[10px] text-[#5C5347]">You</p>
        </div>
        <div className="text-xs text-[#5C5347] font-medium">vs</div>
        <div className="text-center">
          <p className="text-lg font-bold text-[#E24B4A]">{comp.competitor_score_est}</p>
          <p className="text-[10px] text-[#5C5347]">{comp.competitor_name.split(' ').slice(0, 2).join(' ')}</p>
        </div>
      </div>

      {/* Signal table */}
      <div className="space-y-1.5">
        <div className="grid grid-cols-[1fr_50px_50px_50px] gap-1 text-[9px] font-semibold text-[#5C5347] uppercase tracking-wider pb-1 border-b border-[#D4C9B8]">
          <span>Signal</span>
          <span className="text-center">You</span>
          <span className="text-center">Them</span>
          <span className="text-center">Gap</span>
        </div>
        {(comp.signals || []).map((sig) => {
          const severityColor = sig.severity === 'ok' ? '#1D6B4F' : sig.severity === 'warning' ? '#EF9F27' : '#E24B4A';
          return (
            <div key={sig.label} className="grid grid-cols-[1fr_50px_50px_50px] gap-1 items-center py-1" data-testid={`gap-signal-${sig.label.replace(/\s+/g, '-').toLowerCase()}`}>
              <span className="text-[11px] text-[#3C3226]">{sig.label}</span>
              <span className="text-[11px] text-center font-medium text-[#3C3226]">{sig.user_value}</span>
              <span className="text-[11px] text-center text-[#5C5347]">{sig.comp_value}</span>
              <span className="text-[11px] text-center font-semibold" style={{ color: severityColor }}>
                {sig.gap_display}
                {sig.severity === 'critical' && <XCircle size={9} className="inline ml-0.5" />}
                {sig.severity === 'warning' && <AlertTriangle size={9} className="inline ml-0.5" />}
              </span>
            </div>
          );
        })}
      </div>

      {/* Priority actions */}
      {comp.actions?.length > 0 && (
        <div className="mt-4 pt-3 border-t border-[#D4C9B8]">
          <p className="text-[10px] font-semibold text-[#3C3226] uppercase tracking-wider mb-2">Biggest gaps to close</p>
          <div className="space-y-2">
            {comp.actions.map((action) => (
              <div key={action.priority} className="flex items-start gap-2" data-testid={`gap-action-${action.priority}`}>
                <span className="w-5 h-5 rounded-full bg-[#C67A5C]/10 text-[#C67A5C] text-[10px] font-bold flex items-center justify-center flex-shrink-0">{action.priority}</span>
                <div className="flex-1">
                  <p className="text-xs text-[#3C3226]">{action.text}</p>
                  <span className={`text-[9px] font-medium ${action.effort === 'low' ? 'text-[#1D6B4F]' : action.effort === 'medium' ? 'text-[#EF9F27]' : 'text-[#E24B4A]'}`}>
                    {action.effort} effort
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
